import React, { useState, useEffect } from 'react';
import { Bluetooth, Printer, CheckCircle2, AlertCircle, RefreshCw, Smartphone, Tag, Edit2, Save, X, Usb, Cable } from 'lucide-react';
import { executePrint, setActiveBluetoothMac, resetBluetoothConnectionState } from '../utils/printManager';
import { requestUsbPrinter, getSavedUsbPrinter, disconnectUsbPrinter, isWebUSBSupported } from '../utils/usbPrintManager';

export default function ThermalPrinter({
  storeName = "",
  storeAddress = "",
  storePhone = "",
  storeLogoUrl = "",
  footerNote = "",
}) {
  const [devices, setDevices] = useState([]);
  const [status, setStatus] = useState('Belum Terhubung');
  const [isScanning, setIsScanning] = useState(false);
  const [connectedDevice, setConnectedDevice] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // USB OTG Printer State
  const [usbPrinter, setUsbPrinter] = useState(null);
  const [usbLoading, setUsbLoading] = useState(false);

  // Fitur Penamaan / Alias Printer (contoh: "Printer Kasir Depan", "Printer Dapur")
  const [aliases, setAliases] = useState({});
  const [editingAddress, setEditingAddress] = useState(null);
  const [aliasInput, setAliasInput] = useState('');

  // 1. Cek printer tersimpan & alias saat pertama kali dibuka
  useEffect(() => {
    try {
      const savedAliases = localStorage.getItem('savedPrinterAliases');
      if (savedAliases) {
        setAliases(JSON.parse(savedAliases));
      }
    } catch (e) {
      console.error("Gagal membaca alias printer");
    }

    // Cek USB Printer tersimpan
    const savedUsb = getSavedUsbPrinter();
    if (savedUsb) {
      setUsbPrinter(savedUsb);
    }

    const saved = localStorage.getItem('savedBluetoothPrinter');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConnectedDevice(parsed);
        const displayName = parsed.alias || parsed.name;
        setStatus(`Ada printer Bluetooth tersimpan: ${displayName}. Silakan coba cetak.`);
      } catch (e) {
        console.error("Gagal membaca printer tersimpan");
      }
    }
  }, []);

  const handleConnectUsb = async () => {
    setUsbLoading(true);
    try {
      const printer = await requestUsbPrinter();
      setUsbPrinter(printer);
      setStatus(`Printer USB OTG Terhubung: ${printer.productName}`);
    } catch (err) {
      setStatus(`Gagal menghubungkan USB: ${err.message}`);
    } finally {
      setUsbLoading(false);
    }
  };

  const handleDisconnectUsb = async () => {
    await disconnectUsbPrinter();
    setUsbPrinter(null);
    setStatus("Printer USB OTG dilepas.");
  };

  // Simpan alias ke localStorage
  const saveAlias = (address, originalName) => {
    const trimmed = aliasInput.trim();
    const updated = { ...aliases, [address]: trimmed };
    setAliases(updated);
    localStorage.setItem('savedPrinterAliases', JSON.stringify(updated));

    // Jika printer yang diedit adalah printer yang sedang terhubung/tersimpan, perbarui juga
    if (connectedDevice && connectedDevice.address === address) {
      const updatedDevice = { ...connectedDevice, alias: trimmed };
      setConnectedDevice(updatedDevice);
      localStorage.setItem('savedBluetoothPrinter', JSON.stringify(updatedDevice));
    }

    setEditingAddress(null);
    setAliasInput('');
  };

  // 2. Fungsi mencari perangkat Bluetooth (Printer)
  const scanDevices = () => {
    setIsScanning(true);
    setStatus('Memeriksa izin perangkat sekitar...');

    const startScanning = () => {
      setStatus('Mencari printer Bluetooth...');
      window.bluetoothSerial.enable(
        () => {
          window.bluetoothSerial.list(
            (list) => {
              setDevices(list);
              setStatus('Pencarian selesai.');
              setIsScanning(false);
            },
            (err) => {
              setStatus('Gagal mencari perangkat: ' + err);
              setIsScanning(false);
            }
          );
        },
        () => {
          setStatus('Izin Bluetooth ditolak atau Bluetooth belum aktif. Harap izinkan lewat pengaturan.');
          setIsScanning(false);
        }
      );
    };

    if (window.cordova && window.cordova.plugins && window.cordova.plugins.permissions) {
      const permissions = window.cordova.plugins.permissions;
      const list = [
        "android.permission.BLUETOOTH_CONNECT",
        "android.permission.BLUETOOTH_SCAN"
      ];
      permissions.requestPermissions(
        list,
        () => {
          startScanning();
        },
        () => {
          startScanning();
        }
      );
    } else {
      if (window.bluetoothSerial) {
        startScanning();
      } else {
        setStatus('Plugin Bluetooth tidak ditemukan. Pastikan aplikasi berjalan sebagai APK di Android.');
        setIsScanning(false);
      }
    }
  };

  // 3. Fungsi menghubungkan aplikasi ke MAC Address Printer
  const connectPrinter = (device) => {
    setIsConnecting(true);
    const aliasName = aliases[device.address] || device.name;
    setStatus(`Menghubungkan ke ${aliasName}...`);
    
    if (window.bluetoothSerial) {
      window.bluetoothSerial.connect(
        device.address,
        () => {
          setStatus(`Terhubung ke printer ${aliasName}!`);
          const deviceToSave = { ...device, alias: aliases[device.address] || '' };
          setConnectedDevice(deviceToSave);
          localStorage.setItem('savedBluetoothPrinter', JSON.stringify(deviceToSave));
          setActiveBluetoothMac(device.address);
          setIsConnecting(false);
        },
        (err) => {
          setStatus('Gagal terhubung: ' + err);
          setConnectedDevice(null);
          resetBluetoothConnectionState();
          setIsConnecting(false);
        }
      );
    } else {
      setStatus('Plugin Bluetooth tidak ditemukan.');
      setIsConnecting(false);
    }
  };

  // 4. Fungsi membuat format struk dan mencetaknya
  const printReceipt = () => {
    if (!connectedDevice) {
      setStatus('Hubungkan printer terlebih dahulu sebelum mencetak.');
      return;
    }

    setStatus('Mencetak struk test...');
    
    const finalStoreName = storeName || localStorage.getItem('tenantName') || 'ASA LINK';
    const finalStoreAddress = storeAddress || localStorage.getItem('tenantAddress') || 'Jl. Contoh Alamat No. 123';
    const finalStorePhone = storePhone || localStorage.getItem('tenantPhone') || '081234567890';
    const finalStoreLogoUrl = storeLogoUrl || localStorage.getItem('tenantLogoUrl') || '';
    const finalFooterNote = footerNote || localStorage.getItem('tenantFooterNote') || 'Terima Kasih Telah Menjadi Pelanggan Kami';
    
    const dummyData = {
      title: 'Nota TRX-TEST',
      storeName: finalStoreName,
      storeAddress: finalStoreAddress,
      storePhone: finalStorePhone,
      storeLogoUrl: finalStoreLogoUrl,
      storeFooterNote: finalFooterNote,
      meta: {
        transactionNumber: 'TRX-TEST',
        transactionDateTime: new Date().toLocaleString('id-ID'),
        cashierName: 'Admin',
        customerName: 'Walk-in',
      },
      items: [
        { name: 'Nasi Goreng Spesial', qty: 2, price: 15000, total: 30000 },
        { name: 'Es Teh Manis', qty: 3, price: 5000, total: 15000 }
      ],
      totals: {
        subtotal: 45000,
        total: 45000,
        tunai: 50000,
        change: 5000,
        paymentMethodLabel: 'Cash'
      }
    };
    
    executePrint(dummyData, 'transaction')
      .then(() => setStatus('Cetak Berhasil!'))
      .catch(err => setStatus('Gagal cetak: ' + err.message));
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-800 font-semibold">
          <Printer className="w-5 h-5 text-teal-600" />
          <h2>Printer Thermal Bluetooth (Android)</h2>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {connectedDevice ? (
            <span className="flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Terhubung: {connectedDevice.alias || connectedDevice.name}
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full font-medium">
              <AlertCircle className="w-4 h-4" />
              Belum Terhubung
            </span>
          )}
        </div>
      </div>
      
      <div className="p-6">
        <div className="mb-6 p-4 rounded-lg bg-slate-50 border border-slate-100 flex items-start gap-3 text-sm text-slate-600">
          <Smartphone className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-700 mb-1">Status Sistem:</p>
            <p>{status}</p>
          </div>
        </div>

        {/* Section 1: USB OTG Direct Printing (Bebas Watermark & Tanpa Aplikasi Pihak Ketiga) */}
        <div className="mb-6 p-4 rounded-xl bg-slate-50 border border-slate-200">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Usb className="w-5 h-5 text-teal-600" />
              <div>
                <h3 className="text-sm font-bold text-slate-800">Printer Kabel USB OTG (Koneksi Langsung)</h3>
                <p className="text-xs text-slate-500">Cetak langsung melalui koneksi kabel USB OTG secara cepat dan praktis.</p>
              </div>
            </div>
            {usbPrinter ? (
              <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0">
                <CheckCircle2 className="w-3.5 h-3.5" />
                USB Terhubung
              </span>
            ) : (
              <span className="flex items-center gap-1 text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full text-xs font-medium shrink-0">
                Belum Ada USB
              </span>
            )}
          </div>

          {usbPrinter ? (
            <div className="flex items-center justify-between p-3 bg-white border border-emerald-200 rounded-lg">
              <div>
                <p className="text-xs font-bold text-slate-800">{usbPrinter.productName}</p>
                <p className="text-[11px] text-slate-500 font-mono">Vendor ID: {usbPrinter.vendorId} • Product ID: {usbPrinter.productId}</p>
              </div>
              <button
                onClick={handleDisconnectUsb}
                className="px-3 py-1.5 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded-md text-xs font-semibold transition-colors">
                Lepas USB
              </button>
            </div>
          ) : (
            <button
              onClick={handleConnectUsb}
              disabled={usbLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-semibold transition-colors shadow-sm disabled:opacity-50">
              <Cable className="w-4 h-4" />
              {usbLoading ? "Menghubungkan USB..." : "Sambungkan Printer USB OTG"}
            </button>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-medium text-slate-800">Perangkat Tersedia</h3>
                <p className="text-xs text-slate-500 mt-0.5">Anda dapat memberi nama/alias khusus pada setiap printer (misal: Printer Dapur, Printer Kasir Depan).</p>
              </div>
              <button 
                onClick={scanDevices} 
                disabled={isScanning}
                className="flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors text-sm font-medium disabled:opacity-50 shrink-0"
              >
                <RefreshCw className={`w-4 h-4 ${isScanning ? 'animate-spin' : ''}`} />
                {isScanning ? 'Mencari...' : 'Cari Printer'}
              </button>
            </div>
            
            {devices.length === 0 ? (
              <div className="text-center py-8 text-slate-500 border-2 border-dashed border-slate-200 rounded-xl">
                <Bluetooth className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">Belum ada perangkat yang ditemukan.</p>
                <p className="text-xs mt-1">Klik "Cari Printer" untuk memindai perangkat Bluetooth.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {devices.map((device, index) => {
                  const customAlias = aliases[device.address];
                  const isEditing = editingAddress === device.address;
                  const isConnected = connectedDevice && connectedDevice.address === device.address;

                  return (
                    <div key={index} className="p-3.5 border border-slate-200 rounded-xl hover:border-teal-300 bg-white transition-all shadow-sm">
                      {isEditing ? (
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                            <Tag className="w-3.5 h-3.5 text-teal-600" />
                            Nama / Alias Printer:
                          </label>
                          <div className="flex items-center gap-2">
                            <input 
                              type="text" 
                              value={aliasInput}
                              onChange={(e) => setAliasInput(e.target.value)}
                              placeholder="Contoh: Printer Kasir Depan"
                              className="flex-1 px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-teal-500"
                              autoFocus
                            />
                            <button
                              onClick={() => saveAlias(device.address, device.name)}
                              className="p-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                              title="Simpan Nama">
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setEditingAddress(null); setAliasInput(''); }}
                              className="p-1.5 bg-slate-200 text-slate-600 rounded-lg hover:bg-slate-300 transition-colors"
                              title="Batal">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          <p className="text-[11px] text-slate-400 font-mono">Bawaan Bluetooth: {device.name} ({device.address})</p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="font-bold text-slate-800 text-sm truncate">
                                {customAlias || device.name || 'Unknown Device'}
                              </p>
                              {customAlias && (
                                <span className="px-1.5 py-0.5 bg-teal-50 text-teal-700 text-[10px] font-semibold rounded border border-teal-200 shrink-0">
                                  Custom Name
                                </span>
                              )}
                              <button
                                onClick={() => {
                                  setEditingAddress(device.address);
                                  setAliasInput(customAlias || '');
                                }}
                                className="p-1 text-slate-400 hover:text-teal-600 hover:bg-slate-100 rounded transition-colors shrink-0"
                                title="Beri nama / edit alias printer ini">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <p className="text-xs text-slate-500 font-mono mt-0.5 truncate">
                              {customAlias ? `${device.name || 'BT Device'} • ${device.address}` : device.address}
                            </p>
                          </div>
                          <button 
                            onClick={() => connectPrinter(device)}
                            disabled={isConnecting || isConnected}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors shrink-0 ${
                              isConnected
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 border border-slate-200 text-slate-700 hover:bg-teal-50 hover:text-teal-700 hover:border-teal-300'
                            }`}
                          >
                            {isConnected ? 'Terhubung' : 'Hubungkan'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="pt-6 border-t border-slate-200">
            <button 
              onClick={printReceipt} 
              disabled={!connectedDevice}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shadow-teal-600/20"
            >
              <Printer className="w-5 h-5" />
              Test Cetak Struk ({connectedDevice?.alias || connectedDevice?.name || 'Printer'})
            </button>
            {!connectedDevice && (
               <p className="text-center text-xs text-amber-600 mt-2">
                 * Harap hubungkan printer terlebih dahulu
               </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
